<template>
  <div>
    <v-dialog
      v-model="show"
      max-width="60vh"
      scrollable
    >
      <v-card>
        <v-card-title>
          Military Categories
          <v-spacer></v-spacer>
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            label="Search"
            single-line
            hide-details
            dense
            class="mr-4"
          ></v-text-field>
          <v-btn
            icon
            @click="show = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="py-3">
          <v-data-table
            dense
            fixed-header
            height="50vh"
            :headers="tableHeaders"
            :items="militaryCategories"
            :search="search"
            :items-per-page="10"
            :footer-props="{
              'items-per-page-options': [10, 20, 50, -1]
            }"
          >
            <template v-slot:[`item.Category_Name`]="{ item }">
              <v-text-field
                dense
                hide-details="auto"
                :rules="[rules.required, rules.alreadyExist]"
                v-if="editMode && currentItem && currentItem.Military_Category_ID === item.Military_Category_ID"
                v-model="currentItem.Category_Name"
              ></v-text-field>
              <span v-else>{{ item.Category_Name }}</span>
            </template>
            <template v-slot:[`item.Actions`]="{ item }">
              <v-btn
                :disabled="editMode && (currentItem.Military_Category_ID !== item.Military_Category_ID) || (currentItem && currentItem.Military_Category_ID === item.Military_Category_ID && canUpdate)"
                :color="editMode && currentItem.Military_Category_ID === item.Military_Category_ID ? 'success' : 'primary'"
                small
                icon
                @click="onEditClick(item)"
              >
                <v-icon>
                  {{ editMode && currentItem.Military_Category_ID === item.Military_Category_ID ? 'mdi-check-circle' : 'mdi-pencil-circle' }}
                </v-icon>
              </v-btn>
              <v-btn
                small
                icon
                color="red darken-2"
                v-if="!editMode || currentItem.Military_Category_ID !== item.Military_Category_ID"
                :disabled="editMode"
                @click="onDeleteClick(item)"
              >
                <v-icon>mdi-delete-circle</v-icon>
              </v-btn>
              <v-btn
                v-if="editMode && currentItem.Military_Category_ID === item.Military_Category_ID"
                color="primary"
                small
                icon
                @click="onCancelClick"
              >
                <v-icon>mdi-close-circle</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="success"
            small
            :disabled="editMode"
            @click="createNewCategoryDialog = !createNewCategoryDialog"
          >
            Create New
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      max-width="500"
      v-model="createNewCategoryDialog"
    >
      <v-card>
        <v-card-title>
          New Military Category
          <v-spacer></v-spacer>
          <v-btn
            icon
            @click="createNewCategoryDialog = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-4">
          <v-text-field
            v-model="newCategoryName"
            label="Category Name"
            autofocus
            :rules="[rules.required, rules.alreadyExist]"
          ></v-text-field>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            small
            color="primary"
            :disabled="canSave"
            @click="createNewMilitaryCategory"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <ConfirmOrDeniedDialog
      v-if="deleteCategoryDialog"
      v-model="deleteCategoryDialog"
      :mode="confirmOrDeniedDialogModes.delete"
      @onDeleteClick="deleteMilitaryCategory"
      @onCancelClick="cancelDelete"
    />
  </div>
</template>
<script>
import { getApiErrorMessage } from '@/utils/apiError'
export default {
  name: 'MilitaryCategoriesManager',
  components: {
    ConfirmOrDeniedDialog: () => import('@/components/global/ConfirmOrDeniedDialog.vue')
  },
  props: {
    value: {
      required: false,
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      search: '',
      currentItem: null,
      createNewCategoryDialog: false,
      militaryCategories: [],
      deleteCategoryDialog: false,
      editMode: false,
      newCategoryName: '',
      tableHeaders: [
        {
          text: 'Category Name',
          value: 'Category_Name',
          divider: true,
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap'
        },
        {
          text: '#',
          value: 'Actions',
          align: 'center',
          cellClass: 'px-2 text-no-wrap',
          class: 'px-2 text-no-wrap',
          width: 100,
          sortable: false
        }
      ],
      rules: {
        required: v => !!v || 'Required',
        alreadyExist: (val) => this.validateOnAlreadyExist(val) || 'The category name already exists'
      }
    }
  },
  mounted () {
    this.getMilitaryCategories()
  },
  computed: {
    user () {
      return this.$store.getters.getUser
    },
    show: {
      get () {
        return this.value
      },
      set (value) {
        this.$emit('input', value)
      }
    },
    canUpdate () {
      if (!this.currentItem || !this.currentItem.Category_Name) return true
      return !this.currentItem.Category_Name?.trim().length || !this.validateOnAlreadyExist(this.currentItem.Category_Name, this.currentItem.Military_Category_ID)
    },
    canSave () {
      return !this.newCategoryName || !this.newCategoryName?.trim().length || !this.validateOnAlreadyExist(this.newCategoryName)
    },
    confirmOrDeniedDialogModes () {
      return this.$store.getters.getConfirmOrDeniedDialogModes
    }
  },
  methods: {
    validateOnAlreadyExist (val, id = null) {
      if (!val) return true
      return !this.militaryCategories.some(el =>
        el.Category_Name.toLowerCase() === val.trim().toLowerCase() && el.Military_Category_ID !== id
      )
    },
    onEditClick (item) {
      if (this.editMode) {
        this.updateMilitaryCategory()
      } else {
        this.tableHeaders[0].sortable = false
        this.currentItem = Object.assign({}, item)
        this.editMode = true
      }
    },
    onCancelClick () {
      this.editMode = false
      this.currentItem = null
      this.tableHeaders[0].sortable = true
    },
    onDeleteClick (item) {
      this.deleteCategoryDialog = true
      this.currentItem = Object.assign({}, item)
    },
    cancelDelete () {
      this.deleteCategoryDialog = false
      this.currentItem = null
    },
    getMilitaryCategories () {
      this.axios.get('/api/military_categories', {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.militaryCategories = res.data
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    createNewMilitaryCategory () {
      this.axios.post('/api/military_category', {
        Category_Name: this.newCategoryName.trim()
      }, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.createNewCategoryDialog = false
        this.newCategoryName = ''
        this.getMilitaryCategories()
        this.$store.dispatch('setSuccess', { successMsg: 'New military category successfully created' })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    updateMilitaryCategory () {
      this.axios.put(`/api/military_category/${this.currentItem.Military_Category_ID}`, {
        Category_Name: this.currentItem.Category_Name.trim()
      }, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.getMilitaryCategories()
        this.onCancelClick()
        this.$store.dispatch('setSuccess', { successMsg: 'Military category successfully updated' })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    deleteMilitaryCategory () {
      this.axios.delete(`/api/military_category/${this.currentItem.Military_Category_ID}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.getMilitaryCategories()
        this.cancelDelete()
        this.$store.dispatch('setSuccess', { successMsg: 'Military category successfully deleted' })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)

        if (errorMsg.includes('conflicted with the REFERENCE constraint')) {
          this.$store.dispatch('setError', { errorMsg: 'Cannot delete category because it is used in another module.' })
        } else {
          this.$store.dispatch('setError', { errorMsg })
        }
      })
    }
  }
}
</script>
